import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axiosConfig';
 
/**
 * @param {object} options
 * @param {string} options.permission  - required permission key e.g. 'project.view'
 * @param {string} options.url         - API endpoint e.g. '/projects'
 * @param {*}      [options.fallback]  - value to return when access denied (default [])
 *
 * @returns {{ data, loading, error, refetch }}
 */
export const usePermissionFetch = ({ permission, url, fallback = [], lazy = false }) => {
  // Pull loading + checkAccess from AuthContext.
  // When loading flips false → effect re-runs → fetch fires.
  const { loading: authLoading, checkAccess, user } = useAuth();
 
  const [data, setData]       = useState(fallback);
  const [loading, setLoading] = useState(!lazy);
  const [error, setError]     = useState(null);
  const mountedRef            = useRef(true);
 
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
 
  const fetchData = useCallback(async () => {
    // ── WAIT for auth to be ready and user to be logged in ───────────────────
    if (authLoading || !user) {
      if (!user && mountedRef.current) {
        setLoading(false);
      }
      return;
    }
 
    // ── PERMISSION CHECK ─────────────────────────────────────────────────────
    if (!checkAccess(permission)) {
      if (mountedRef.current) {
        setData(fallback);
        setLoading(false);
      }
      return;
    }
 
    // ── FETCH ────────────────────────────────────────────────────────────────
    setLoading(true);
    setError(null);
 
    try {
      const res = await api.get(url);
      const payload = res.data?.data !== undefined ? res.data.data : res.data;
      if (mountedRef.current) setData(payload);
    } catch (err) {
      if (mountedRef.current) {
        // 401/403 = permission blocked at backend level — handle silently
        if (err.response?.status === 401 || err.response?.status === 403) {
          setData(fallback);
        } else {
          setError(err.message);
          console.error(`usePermissionFetch [${url}]:`, err.message);
        }
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [authLoading, user, permission, url]);
 
  useEffect(() => {
    if (!lazy) {
      fetchData();
    }
  }, [fetchData, lazy]);
 
  return { data, loading, error, refetch: fetchData };
};
 
export default usePermissionFetch;